import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

export const AttendanceTrackingDialog = ({
  open,
  onOpenChange,
  attendanceAppointment,
  attendanceData,
  setAttendanceData,
  clientGroups,
  clients,
  onSaveAttendance,
  // New: Direct group mode (no appointment)
  directGroup,
  sessionDate,
  setSessionDate
}) => {
  // Find the group - either from appointment or directly passed
  const group = directGroup || clientGroups?.find(g => g.id === attendanceAppointment?.selected_group_id);
  const isDirectMode = !!directGroup && !attendanceAppointment;
  
  // Debug logging
  React.useEffect(() => {
    if (open && group) {
      void 0 && console.log('[AttendanceDialog] Group:', group.name);
      void 0 && console.log('[AttendanceDialog] Group members array:', group.members);
      void 0 && console.log('[AttendanceDialog] Group client_ids:', group.client_ids);
      void 0 && console.log('[AttendanceDialog] Clients array length:', clients?.length);
      void 0 && console.log('[AttendanceDialog] First few clients:', clients?.slice(0, 3).map(c => ({ id: c.id, name: c.full_name })));
    }
  }, [open, group, clients]);
  
  // Get active members from the group
  // Members can be in group.members array (with status) or group.client_ids array
  const getGroupMembers = () => {
    if (!group) {
      void 0 && console.log('[AttendanceDialog] No group found');
      return [];
    }
    
    // First, try to get members from the members array (which has status and client_details)
    if (group.members && group.members.length > 0) {
      void 0 && console.log('[AttendanceDialog] Using members array, length:', group.members.length);
      
      // Filter to only active members (not removed/discharged)
      const activeMembers = group.members
        .filter(m => {
          const isActive = m.status === 'active' || !m.status;
          void 0 && console.log(`[AttendanceDialog] Member ${m.client_id}: status=${m.status}, isActive=${isActive}`);
          return isActive;
        })
        .map(m => {
          // First try embedded client_details from backend (most reliable)
          if (m.client_details) {
            void 0 && console.log(`[AttendanceDialog] Using embedded client_details for ${m.client_id}: ${m.client_details.full_name}`);
            return { 
              id: m.client_id, 
              full_name: m.client_details.full_name,
              email: m.client_details.email,
              mrn: m.client_details.mrn,
              memberInfo: m 
            };
          }
          
          // Fallback to clients lookup
          const client = clients?.find(c => c.id === m.client_id);
          if (client) {
            void 0 && console.log(`[AttendanceDialog] Found client via lookup for ${m.client_id}: ${client.full_name}`);
            return { ...client, memberInfo: m };
          }
          
          // Last resort: return minimal info so member still shows
          void 0 && console.log(`[AttendanceDialog] Client ${m.client_id} not found, using minimal info`);
          return { 
            id: m.client_id, 
            full_name: m.client_name || `Client ${m.client_id.substring(0, 8)}...`,
            memberInfo: m 
          };
        })
        .filter(Boolean);
      
      void 0 && console.log('[AttendanceDialog] Active members found:', activeMembers.length);
      return activeMembers;
    }
    
    // Fallback to client_ids array
    if (group.client_ids && group.client_ids.length > 0) {
      void 0 && console.log('[AttendanceDialog] Using client_ids array, length:', group.client_ids.length);
      
      const membersFromIds = group.client_ids
        .map(clientId => {
          const client = clients?.find(c => c.id === clientId);
          if (client) {
            void 0 && console.log(`[AttendanceDialog] Found client ${clientId} via lookup: ${client.full_name}`);
            return client;
          }
          // Fallback: return minimal info
          void 0 && console.log(`[AttendanceDialog] Client ${clientId} not found, using minimal info`);
          return { id: clientId, full_name: `Client ${clientId.substring(0, 8)}...` };
        })
        .filter(Boolean);
      
      void 0 && console.log('[AttendanceDialog] Members from client_ids:', membersFromIds.length);
      return membersFromIds;
    }
    
    void 0 && console.log('[AttendanceDialog] No members or client_ids found in group');
    return [];
  };
  
  const activeMembers = getGroupMembers()
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Track Attendance</DialogTitle>
          <DialogDescription>
            {isDirectMode 
              ? `Record attendance for ${group?.name || 'Group Session'}`
              : `Mark attendance for ${attendanceAppointment?.title || 'Group Session'}${group ? ` - ${group.name}` : ''}`
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Session Date - only in direct mode (no appointment) */}
          {isDirectMode && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <Label className="text-sm font-medium text-yellow-800">Session Date</Label>
              <Input 
                type="date" 
                value={sessionDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => setSessionDate && setSessionDate(e.target.value)}
                className="mt-1 max-w-[200px]"
              />
              <p className="text-xs text-yellow-700 mt-1">
                Recording attendance without a linked appointment
              </p>
            </div>
          )}
          
          {group && activeMembers.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-2">
                Group Members ({activeMembers.length})
              </p>
              {activeMembers.map(client => {
                const clientId = client.id;
                // Look up eligibility from the clients list (has MINIMAL_FIELDS including eligibility)
                const clientWithElig = clients?.find(c => c.id === clientId) || client;
                const eligStatus = clientWithElig?.primary_eligibility_status || 'not_checked';
                const dos = (isDirectMode ? sessionDate : attendanceAppointment?.start_time?.substring(0, 10)) || null;
                const ELIG_ICON = { verified: '✓', active: '✓', rejected: '✗', inactive: '✗', pending: '⏳', submitted: '⏳', queued: '⏳' };
                const ELIG_COLOR = { verified: 'text-green-600', active: 'text-green-600', rejected: 'text-red-600', inactive: 'text-red-600', pending: 'text-yellow-600', submitted: 'text-yellow-600', queued: 'text-yellow-600' };
                // DOS check: if verified but outside coverage window, show warning
                let displayStatus = eligStatus;
                if ((eligStatus === 'verified' || eligStatus === 'active') && dos) {
                  const eff = clientWithElig?.primary_effective_date;
                  const end = clientWithElig?.primary_end_date;
                  if (eff && dos < eff) displayStatus = 'pre_coverage';
                  else if (end && dos > end) displayStatus = 'expired';
                }
                const eligIcon = displayStatus === 'pre_coverage' ? '⚠' : displayStatus === 'expired' ? '✗' : (ELIG_ICON[eligStatus] || '');
                const eligColor = displayStatus === 'pre_coverage' ? 'text-amber-500' : (ELIG_COLOR[eligStatus] || '');
                const lastDOS = clientWithElig?.last_eligibility_dos;
                const lastChecked = clientWithElig?.last_eligibility_check;
                const tooltipLines = [];
                if (eligIcon) {
                  const labelMap = { verified: 'Eligible', active: 'Eligible', rejected: 'Rejected', inactive: 'Not Eligible', pending: 'Pending', submitted: 'Pending', queued: 'Pending', pre_coverage: 'Before Coverage Start', expired: 'Coverage Expired' };
                  tooltipLines.push(labelMap[displayStatus] || eligStatus);
                }
                if (lastDOS) tooltipLines.push(`Checking for: ${lastDOS}`);
                if (lastChecked) tooltipLines.push(`Checked: ${new Date(lastChecked).toLocaleDateString()}`);
                if (clientWithElig?.primary_effective_date) tooltipLines.push(`Coverage: ${clientWithElig.primary_effective_date}${clientWithElig.primary_end_date ? ` → ${clientWithElig.primary_end_date}` : ''}`);
                
                return (
                  <div key={clientId} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                    <div>
                      <p className="font-medium flex items-center gap-1">
                        {client.full_name}
                        {eligIcon && (
                          <span className={`text-xs font-bold ${eligColor}`} title={tooltipLines.join('\n')}>
                            {eligIcon}
                          </span>
                        )}
                      </p>
                      {displayStatus === 'pre_coverage' && (
                        <p className="text-[10px] text-amber-600">Before coverage start</p>
                      )}
                      {displayStatus === 'expired' && (
                        <p className="text-[10px] text-red-600">Coverage expired</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={attendanceData[clientId] === 'attended' ? 'default' : 'outline'}
                        className={attendanceData[clientId] === 'attended' ? 'bg-green-600 hover:bg-green-700' : ''}
                        onClick={() => setAttendanceData({...attendanceData, [clientId]: 'attended'})}
                      >
                        ✓ Attended
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={attendanceData[clientId] === 'no_show' ? 'default' : 'outline'}
                        className={attendanceData[clientId] === 'no_show' ? 'bg-red-600 hover:bg-red-700' : ''}
                        onClick={() => setAttendanceData({...attendanceData, [clientId]: 'no_show'})}
                      >
                        ✗ No Show
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={attendanceData[clientId] === 'cancelled' ? 'default' : 'outline'}
                        className={attendanceData[clientId] === 'cancelled' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
                        onClick={() => setAttendanceData({...attendanceData, [clientId]: 'cancelled'})}
                      >
                        ⊘ Cancelled
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              {!group ? (
                <p className="text-gray-500">
                  {isDirectMode ? 'Group not found' : 'No group session selected'}
                </p>
              ) : (
                <div>
                  <p className="text-gray-500 mb-2">No active members in this group</p>
                  <p className="text-xs text-gray-400">
                    Group ID: {group?.id || attendanceAppointment?.selected_group_id || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Members array: {group?.members?.length || 0}, Client IDs: {group?.client_ids?.length || 0}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Clients available: {clients?.length || 0}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-2 mt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            type="button" 
            onClick={onSaveAttendance}
            disabled={activeMembers.length === 0}
          >
            Save Attendance
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceTrackingDialog;
